import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { Alert, Button, Input, Select, Textarea, Badge, Accordion, SearchField } from '@dreamtree-org/twreact-ui';
import { Search, Plus, Minus, Trash2, Send, Save, CheckCircle2, XCircle } from 'lucide-react';
import { PageHeader, SectionCard, QueryState, KeyValue, Money } from '@/components';
import { useQuery } from '@/lib/useQuery';
import { formatINR } from '@/lib/format';
import { FAULT_CODES, quoteTotals, latestEstimate } from '../domain';
import { EstimateBadge, StageBadge, RestrictionsBanner, useAction, ActionResult, formatDateTime } from '../components';

let localLineSeq = 1;
const newLineId = () => `new-${localLineSeq++}`;

function Totals({ totals }) {
  return (
    <KeyValue
      items={[
        { label: 'Parts', value: <Money paise={totals.partsPaise} /> },
        { label: 'Labour', value: <Money paise={totals.labourPaise} /> },
        { label: 'GST', value: <Money paise={totals.taxPaise} /> },
        { label: 'Total', value: <Money paise={totals.totalPaise} className="text-lg font-bold" /> },
      ]}
    />
  );
}

function FindingsCard({ job, act, reload }) {
  const [findings, setFindings] = useState(job.findings || '');
  const [codes, setCodes] = useState(job.faultCodes || []);
  const locked = !['diagnosis', 'awaiting_approval'].includes(job.stage);
  const save = async () => {
    const res = await act.run('serviceJobDiagnosis', { id: job.id, findings, faultCodes: codes }, 'Diagnosis saved.');
    if (res) reload();
  };
  return (
    <SectionCard title="Diagnostic findings">
      <Textarea
        label="Findings"
        rows={4}
        autoResize
        value={findings}
        onChange={(e) => setFindings(e.target.value)}
        disabled={locked}
        placeholder="What you tested, what you measured, what failed."
      />
      <Select label="Fault codes" options={FAULT_CODES} value={codes} onChange={(v) => setCodes(v || [])} multiSelect searchable selectAllOption={false} disabled={locked} />
      {!locked ? (
        <Button variant="outline" onClick={save} disabled={!findings.trim()} loading={act.busy === 'serviceJobDiagnosis'}>
          Save diagnosis
        </Button>
      ) : null}
    </SectionCard>
  );
}

function PartPicker({ brand, onAdd }) {
  const [term, setTerm] = useState('');
  const q = useQuery('serviceParts', { q: term }, { select: (r) => r.data });
  const rows = (q.data || []).slice().sort((a, b) => Number(b.brands.includes(brand)) - Number(a.brands.includes(brand))).slice(0, 6);
  return (
    <div className="space-y-2">
      <SearchField
        label="Parts catalog"
        placeholder="Search SKU or part name"
        value={term}
        onChange={(e) => setTerm(e.target.value)}
        leftIcon={<Search size={16} />} />
      <ul className="divide-y divide-[var(--app-border)] rounded-md border border-[var(--app-border)]">
        {rows.map((p) => (
          <li key={p.id} className="flex items-center gap-3 p-2">
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium truncate">{p.description}</div>
              <div className="text-xs opacity-70 flex flex-wrap gap-x-2">
                <span className="font-mono">{p.sku}</span>
                <span>{p.pricePaise ? formatINR(p.pricePaise) : 'consumable'}</span>
                <span>{p.free} free</span>
                {p.brands.includes(brand) ? <span className="text-primary-600">fits {brand}</span> : null}
              </div>
            </div>
            <Button size="sm" variant="outline" onClick={() => onAdd(p)} disabled={p.free <= 0} leftIcon={<Plus size={14} />} aria-label={`Add ${p.sku}`}>
              Add
            </Button>
          </li>
        ))}
        {!q.loading && rows.length === 0 ? <li className="p-3 text-sm opacity-70">No parts match.</li> : null}
      </ul>
    </div>
  );
}

function QuoteBuilder({ job, act, reload }) {
  const latest = latestEstimate(job);
  const editable = !latest || ['draft', 'sent', 'declined'].includes(latest.status);
  const [lines, setLines] = useState(() => (latest ? latest.lines.map((l) => ({ ...l })) : []));
  const [notes, setNotes] = useState(latest && latest.status === 'draft' ? latest.notes : '');
  const [labourDesc, setLabourDesc] = useState('');
  const [labourRupees, setLabourRupees] = useState('');
  const totals = quoteTotals(lines);
  const dirty = !latest || JSON.stringify(lines) !== JSON.stringify(latest.lines);
  const nextVersion = latest && latest.status !== 'draft' ? latest.version + 1 : latest ? latest.version : 1;

  const addPart = (p) =>
    setLines((ls) => {
      const existing = ls.find((l) => l.partId === p.id);
      if (existing) return ls.map((l) => (l === existing ? { ...l, quantity: l.quantity + 1 } : l));
      return [...ls, { id: newLineId(), lineType: 'part', partId: p.id, description: p.description, quantity: 1, unitPricePaise: p.pricePaise, taxRate: 18 }];
    });
  const addLabour = () => {
    const paise = Math.round(Number(labourRupees) * 100);
    if (!labourDesc.trim() || !(paise > 0)) return;
    setLines((ls) => [...ls, { id: newLineId(), lineType: 'labour', description: labourDesc.trim(), quantity: 1, unitPricePaise: paise, taxRate: 18 }]);
    setLabourDesc('');
    setLabourRupees('');
  };
  const setQty = (id, delta) => setLines((ls) => ls.map((l) => (l.id === id ? { ...l, quantity: Math.max(1, l.quantity + delta) } : l)));
  const remove = (id) => setLines((ls) => ls.filter((l) => l.id !== id));

  const save = async () => {
    const res = await act.run('serviceEstimateCreate', { jobId: job.id, lines, notes }, `Quote v${nextVersion} saved as a draft.`);
    if (res) reload();
  };
  const send = async () => {
    const res = await act.run('serviceEstimateSend', { jobId: job.id }, `Quote v${latest.version} sent. The customer gets an approval link by SMS and WhatsApp.`);
    if (res) reload();
  };

  return (
    <SectionCard
      title={latest ? `Quote v${latest.version}` : 'New quote (v1)'}
      actions={latest ? <EstimateBadge status={latest.status} /> : null}
    >
      {editable && latest && latest.status !== 'draft' ? (
        <p className="text-xs opacity-70">Changes are saved as a new revision, v{nextVersion}; v{latest.version} stays in the history.</p>
      ) : null}
      {editable ? <PartPicker brand={job.device.brand} onAdd={addPart} /> : null}

      <div className="space-y-2">
        <div className="text-sm font-medium">Lines</div>
        {lines.length === 0 ? <p className="text-sm opacity-70">Add parts from the catalog and at least one labour line.</p> : null}
        <ul className="divide-y divide-[var(--app-border)]">
          {lines.map((l) => (
            <li key={l.id} className="py-2 flex flex-wrap items-center gap-2">
              <div className="flex-1 min-w-[12rem]">
                <div className="text-sm font-medium">{l.description}</div>
                <div className="text-xs opacity-70 flex gap-2 items-center">
                  <Badge size="xs" variant={l.lineType === 'labour' ? 'secondary' : 'outline'}>
                    {l.lineType === 'labour' ? 'Labour' : 'Part'}
                  </Badge>
                  {formatINR(l.unitPricePaise)} each · GST {l.taxRate}%
                </div>
              </div>
              <div className="flex items-center gap-1">
                {editable ? (
                  <Button size="sm" variant="ghost" onClick={() => setQty(l.id, -1)} aria-label="Decrease quantity" disabled={l.quantity <= 1}>
                    <Minus size={14} />
                  </Button>
                ) : null}
                <span className="w-8 text-center tabular-nums">{l.quantity}</span>
                {editable ? (
                  <Button size="sm" variant="ghost" onClick={() => setQty(l.id, 1)} aria-label="Increase quantity">
                    <Plus size={14} />
                  </Button>
                ) : null}
              </div>
              <Money paise={l.quantity * l.unitPricePaise} className="w-24 text-right tabular-nums font-medium" />
              {editable ? (
                <Button size="sm" variant="ghost" onClick={() => remove(l.id)} aria-label={`Remove ${l.description}`}>
                  <Trash2 size={14} />
                </Button>
              ) : null}
            </li>
          ))}
        </ul>
      </div>

      {editable ? (
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-[1fr_9rem_auto] items-end">
          <Input label="Labour line" value={labourDesc} onChange={(e) => setLabourDesc(e.target.value)} placeholder="e.g. Board-level repair" />
          <Input label="Amount (₹)" type="number" value={labourRupees} onChange={(e) => setLabourRupees(e.target.value)} placeholder="1200" />
          <Button variant="outline" onClick={addLabour} disabled={!labourDesc.trim() || !(Number(labourRupees) > 0)} leftIcon={<Plus size={14} />}>
            Add labour
          </Button>
        </div>
      ) : null}

      <div className="rounded-md bg-[var(--app-bg)] p-3">
        <Totals totals={totals} />
      </div>

      {editable ? (
        <>
          <Input label="Note to customer" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Optional: why this revision" />
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" onClick={save} disabled={!lines.length || (!dirty && latest && (latest.status !== 'draft' || notes === latest.notes))} loading={act.busy === 'serviceEstimateCreate'} leftIcon={<Save size={16} />}>
              {latest && latest.status !== 'draft' ? `Save as revision v${nextVersion}` : 'Save draft'}
            </Button>
            <Button
              onClick={send}
              disabled={!latest || latest.status !== 'draft' || dirty || !job.findings}
              loading={act.busy === 'serviceEstimateSend'}
              leftIcon={<Send size={16} />}
            >
              Send for customer approval
            </Button>
          </div>
          {!job.findings ? <p className="text-xs opacity-70">Save the diagnostic findings before sending a quote.</p> : null}
          {latest && latest.status === 'draft' && dirty ? <p className="text-xs opacity-70">Save your changes before sending.</p> : null}
        </>
      ) : null}
    </SectionCard>
  );
}

function ApprovalStatus({ job, act, reload }) {
  const latest = latestEstimate(job);
  const decide = async (decision) => {
    const res = await act.run('serviceEstimateDecision', { jobId: job.id, version: latest.version, decision }, decision === 'APPROVE' ? `Quote v${latest.version} approved; parts reserved and repair released.` : `Quote v${latest.version} declined; arrange a safe return.`);
    if (res) reload();
  };
  if (!latest) {
    return (
      <Alert variant="info" title="No quote yet">
        Billable repair is blocked until the customer approves a quote.
      </Alert>
    );
  }
  if (latest.status === 'approved') {
    return (
      <Alert variant="success" title={`Customer approved quote v${latest.version}`}>
        Approved {formatDateTime(latest.customerDecisionAt)} for {formatINR(latest.totalPaise)}. Repair is released.
      </Alert>
    );
  }
  if (latest.status === 'declined') {
    return (
      <Alert variant="error" title={`Customer declined quote v${latest.version}`}>
        Revise the quote, or move the job to Ready for return for a safe return. Only the diagnostic fee is billable.
      </Alert>
    );
  }
  if (latest.status === 'sent') {
    return (
      <Alert variant="warning" title={`Waiting for the customer to approve v${latest.version}`}>
        <div className="space-y-2">
          <div>Sent {formatDateTime(latest.sentAt)}. Billable repair is blocked until the customer approves this exact version.</div>
          <div className="text-xs opacity-80">The customer approves or declines from their repair tracker. If they told you at the counter or on the phone, record it here.</div>
          <div className="flex flex-wrap gap-2">
            <Button size="sm" variant="success" onClick={() => decide('APPROVE')} loading={act.busy === 'serviceEstimateDecision'} leftIcon={<CheckCircle2 size={14} />}>
              Record approval
            </Button>
            <Button size="sm" variant="outline" onClick={() => decide('DECLINE')} leftIcon={<XCircle size={14} />}>
              Record decline
            </Button>
          </div>
        </div>
      </Alert>
    );
  }
  return (
    <Alert variant="info" title={`Quote v${latest.version} is a draft`}>
      Billable repair is blocked until the customer approves a sent quote.
    </Alert>
  );
}

/** SERV-02 Diagnostic & quotation (R-03). */
export default function Diagnosis() {
  const { id } = useParams();
  const q = useQuery('serviceJob', { id }, { select: (r) => r.data });
  const act = useAction();

  return (
    <div className="space-y-4">
      <QueryState query={q}>
        {(job) => {
          const latest = latestEstimate(job);
          const history = job.estimates.slice().sort((a, b) => b.version - a.version);
          return (
            <>
              <PageHeader
                title="Diagnosis & quotation"
                subtitle={`${job.id} · ${job.device.model} · ${job.problem}`}
                back={{ label: `Work order ${job.id}`, href: `/jobs/${job.id}` }}
                actions={<StageBadge stage={job.stage} size="md" />}
              />
              <ApprovalStatus job={job} act={act} reload={q.reload} />
              <ActionResult action={act} />
              <div className="grid grid-cols-1 gap-4 lg:grid-cols-5">
                <div className="space-y-4 lg:col-span-3">
                  <FindingsCard key={`f-${job.findings}`} job={job} act={act} reload={q.reload} />
                  <QuoteBuilder key={latest ? `${latest.id}-${latest.status}` : 'none'} job={job} act={act} reload={q.reload} />
                </div>
                <div className="space-y-4 lg:col-span-2">
                  <RestrictionsBanner consent={job.restrictions} />
                  <SectionCard title="Revision history">
                    {history.length === 0 ? (
                      <p className="text-sm opacity-70">No quotes yet.</p>
                    ) : (
                      <Accordion
                        allowMultiple
                        bordered
                        size="sm"
                        items={history.map((e) => ({
                          id: e.id,
                          title: (
                            <span className="flex flex-wrap items-center gap-2">
                              <span className="font-semibold">v{e.version}</span>
                              <EstimateBadge status={e.status} />
                              <span className="tabular-nums">{formatINR(e.totalPaise)}</span>
                            </span>
                          ),
                          subtitle: `Created ${formatDateTime(e.createdAt)}${e.customerDecisionAt ? ` · decided ${formatDateTime(e.customerDecisionAt)}` : ''}`,
                          content: (
                            <div className="space-y-2 text-sm">
                              {e.notes ? <p className="italic opacity-80">{e.notes}</p> : null}
                              <ul className="space-y-1">
                                {e.lines.map((l) => (
                                  <li key={l.id} className="flex justify-between gap-2">
                                    <span className="min-w-0">
                                      {l.quantity} × {l.description}
                                    </span>
                                    <span className="tabular-nums shrink-0">{formatINR(l.quantity * l.unitPricePaise)}</span>
                                  </li>
                                ))}
                              </ul>
                              <Totals totals={e} />
                            </div>
                          ),
                        }))}
                      />
                    )}
                  </SectionCard>
                  <SectionCard title="Device">
                    <KeyValue
                      items={[
                        { label: 'Model', value: job.device.model },
                        { label: 'Serial', value: <span className="font-mono">{job.device.serialMasked}</span> },
                        { label: 'Customer', value: job.customer.name },
                        { label: 'Technician', value: job.technicianName },
                      ]}
                    />
                  </SectionCard>
                </div>
              </div>
            </>
          );
        }}
      </QueryState>
    </div>
  );
}
